<script setup lang="ts">
interface Props {
  text: string | boolean;
  type?: "outline" | "filled";
  color?: "purple" | "yellow" | "pink" | "dark";
  size?: "default" | "large" | "small";
}

withDefaults(defineProps<Props>(), {
  type: "outline",
  color: "pink",
  size: "default",
});
</script>

<template>
  <div
    v-if="text"
    :class="['pill', `pill--${color}`, `pill--${type}`, `pill--${size}`]"
  >
    <p>{{ text }}</p>
  </div>
</template>

<style lang="scss" scoped>
.pill {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-pill);
  line-height: 1;
  & p {
    font-weight: var(--weight-semibold);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin: 0;
    line-height: 1;
  }

  &--default {
    padding: 0.25rem 0.65rem;
    & p {
      font-size: 0.7rem;
    }
  }

  &--small {
    padding: 0.25rem 0.5625rem;
    & p {
      font-size: 0.59375rem;
      line-height: 1 !important;
    }
  }

  // Large variant — sized to sit comfortably next to an h1/h3 heading.
  // Used for the NEW badge on the maker bio and the package-type badge
  // on the premium change-maker page.
  &--large {
    padding: 0.45rem 0.9rem;
    & p {
      font-size: 0.95rem;
    }
  }

  &--pink {
    color: var(--color-pink-1);
    border: solid 1px var(--color-pink-1);
  }

  &--purple {
    color: var(--color-purple-1);
    border: solid 1px var(--color-purple-1);
  }

  &--yellow {
    color: var(--color-yellow-1);
    border: solid 1px var(--color-yellow-1);
  }

  &--dark {
    color: var(--color-grey-1);
    border: solid 1px var(--color-grey-1);
  }

  &--outline {
    background: transparent;
  }

  // Filled variants — solid background, white text, no border. Matches
  // the compact "status" badge style used by the NEW badge on the
  // maker page so the Group Select / Set notifier on change-maker
  // reads as the same visual family.
  &--filled {
    color: var(--color-white-1);
    border-color: transparent !important;

    &.pill--pink {
      background-color: var(--color-pink-1);
    }

    &.pill--purple {
      background-color: var(--color-purple-1);
    }

    &.pill--yellow {
      background-color: var(--color-yellow-1);
    }

    &.pill--dark {
      background-color: rgba(63, 58, 55, 0.9);
    }
  }
}
</style>
